import TileActionResult from 'app/src/engine/action-results/TileActionResult';
import GearTileInteraction from 'app/src/engine/tile-interactions/GearTileInteraction';
import TileInteraction from 'app/src/engine/tile-interactions/TileInteraction';
import GearTile, {
	GearTileDirection,
	GearTileFloorOrientation,
	GearTileFloorType,
	getGearTileDirection,
	getGearTileFloorOrientation,
	getGearTileFloorType,
} from 'app/src/engine/tiles/GearTile';
import Tile from 'app/src/engine/tiles/Tile';
import GearTileNode from 'app/src/game/map/tiles/GearTile';
import GamePlugin, { TileData } from 'app/src/plugins/GamePlugin';
import * as React from 'react';

export enum GearsGamePluginError {
	MissingDirectionInTileDescription,
	InvalidGearTileFloorTypeInTileDescription,
	InvalidGearTileFloorOrientationInTileDescription,
}

export default class GearsGamePlugin extends GamePlugin {
	private _tileInteraction = new GearTileInteraction();

	// Get a list of Tiles that this plugin provides. When the tile grid is
	// being initialized, it'll ask all plugins which Tiles they create, and
	// later use those corresponding plugins to build Tiles.
	public getListOfProvidedTiles(): string[] {
		return ['GearTile'];
	}

	// Based on Tile data, generate a Tile.
	public getTile(data: TileData): Tile {
		const directionName = data['direction'];
		if (typeof directionName !== 'string') {
			throw GearsGamePluginError.MissingDirectionInTileDescription;
		}
		const direction = getGearTileDirection(directionName);

		let floorType: GearTileFloorType | null = null;
		const floorTypeName = data['floorType'];
		if (typeof floorTypeName === 'string') {
			floorType = getGearTileFloorType(floorTypeName);
		}
		if (floorType === null) {
			throw GearsGamePluginError.InvalidGearTileFloorTypeInTileDescription;
		}

		let floorOrientation: GearTileFloorOrientation | null = null;
		const floorOrientationName = data['floorOrientation'];
		if (typeof floorOrientationName === 'string') {
			floorOrientation = getGearTileFloorOrientation(floorOrientationName);
		}
		if (floorOrientation === null) {
			throw GearsGamePluginError.InvalidGearTileFloorOrientationInTileDescription;
		}

		const tile = new GearTile(direction, floorType, floorOrientation);

		const wallsData = data['walls'];
		if (Array.isArray(wallsData)) {
			tile.addWallsFromData(wallsData);
		}

		return tile;
	}

	// If the editor chooses to support this plugin, and if this plugin displays
	// tiles, this list of tiles will be used in the side panel.
	public getCanonicalTiles(): Tile[] {
		return [
			new GearTile(
				GearTileDirection.Clockwise,
				GearTileFloorType.Type1,
				GearTileFloorOrientation.Left,
			),
			new GearTile(
				GearTileDirection.CounterClockwise,
				GearTileFloorType.Type1,
				GearTileFloorOrientation.Left,
			),
		];
	}

	// Given a Tile, is this plugin capable of generating a corresponding TileNode?
	public providesNodeForTile(tile: Tile): boolean {
		return tile instanceof GearTile;
	}

	// Based on a Tile, generate a TileNode.
	public getTileNode(
		tile: Tile,
		row: number,
		col: number,
		actionResults?: TileActionResult[] | null,
		completeAnimation?: () => void,
	): React.ReactElement {
		if (!(tile instanceof GearTile)) {
			throw new Error('Unsupported Tile.');
		}

		return (
			<GearTileNode
				row={row}
				col={col}
				direction={tile.direction}
				floorType={tile.floorType}
				floorOrientation={tile.floorOrientation}
				actionResults={actionResults}
				completeAnimation={completeAnimation}
			/>
		);
	}

	// Get a list of tile interaction plugins that this plugin provides.
	public getTileInteractions(): TileInteraction[] {
		return [this._tileInteraction];
	}
}
